// BotOverview.tsx — the default `/bots/:botId` tab. Task 8 (handoff README §2 "Bot Overview —
// populated") rebuilt the screen owners actually live on: a 60-second session that asks exactly
// two questions — is it working (stats + Latest conversations) and is there anything to deal
// with (Needs you). Task 9 (handoff README §3 "Bot Overview — brand new") added the branch
// below: a bot that has never held a conversation gets `FirstRun` instead — an early return, not
// a fragment swapped into this file's JSX, because the brand-new screen has its own header
// subline and no header actions (see `FirstRun.tsx`'s own header comment). Everything below the
// branch is the populated layout, unchanged by Task 9.
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@repo/ui/components/button';
import { Skeleton, SkeletonText } from '@repo/ui/components/skeleton';
import { StatusChip } from '@repo/ui/components/status-chip';
import type { BotDto, SourceDto } from '@repo/shared-types';
import { FirstRun } from '../components/overview/FirstRun';
import { NeedsYou } from '../components/overview/NeedsYou';
import { PageHeader } from '../components/PageHeader';
import { UsageWarningBanner } from '../components/UsageWarningBanner';
import { useBillingSummary } from '../hooks/useBillingSummary';
import { useBotOverview } from '../hooks/useBotOverview';
import { apiFetch } from '../lib/api';
import { deriveDomain } from '../lib/bots-list';
import {
  compactRelativeTime,
  eligibleForResync,
  knowledgeChipView,
  messagesTrend,
  sparklineArea,
  sparklineDateLabels,
  sparklinePoints,
} from '../lib/overview';
import { relativeTime } from '../lib/relative-time';
import { useLocale } from '../locales/context';
import type { Catalog } from '../locales/en';
import { formatNumber } from '../locales/format';

const SPARK_W = 320;
const SPARK_H = 64;

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/** "answering since 12 June" (handoff §2) — the bot's own `createdAt`, the one date this
 * screen already has without a new fetch. Full month name (not the short "Jun" the sparkline's
 * date-ends use) — matches the handoff's literal copy for this specific line. */
function sinceDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

function StatsSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-[2fr_1fr_1fr]">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex flex-col gap-3 rounded-[14px] border bg-card p-5 shadow-el-1">
          <SkeletonText className="w-[40%]" />
          <Skeleton className="h-[30px] w-[60%]" />
          {i === 0 && <Skeleton className="h-16 w-full" />}
        </div>
      ))}
    </div>
  );
}

function TableSkeleton() {
  const widths = ['w-[92%]', 'w-[78%]', 'w-[85%]', 'w-[64%]'];
  return (
    <div className="flex flex-col overflow-hidden rounded-[14px] border bg-card shadow-el-1">
      {widths.map((w, i) => (
        <div key={w} className={`flex h-11 items-center px-5 ${i === 0 ? '' : 'border-t'}`}>
          <SkeletonText className={w} />
        </div>
      ))}
    </div>
  );
}

/** The right-aligned tag on a Latest-conversations row (handoff §2: "Lead captured" in
 * `--st-ready`, "Has unanswered" in `--st-paused`). A conversation can carry both facts, but
 * the row has room for one tag — a captured lead is the better news and wins, matching the
 * handoff's demo data (never two tags stacked in one 132px-wide slot). `null` when neither
 * applies: an ordinary answered conversation has nothing to flag. */
function conversationTag(
  conv: { leadCaptured: boolean; hasUnanswered: boolean },
  t: Catalog,
): { label: string; className: string } | null {
  if (conv.leadCaptured) return { label: t.overview.leadCapturedTag, className: 'text-st-ready' };
  if (conv.hasUnanswered) return { label: t.overview.hasUnansweredTag, className: 'text-st-paused' };
  return null;
}

export function BotOverview() {
  const { t, locale } = useLocale();
  const { botId } = useParams<{ botId: string }>();
  const queryClient = useQueryClient();

  const { data: bot, error: botError } = useQuery({
    queryKey: ['bots', botId],
    queryFn: () => apiFetch<BotDto>(`/api/bots/${botId}`),
    enabled: !!botId,
  });
  // Fix round 1 — `!bot` used to double as both "still loading" and "failed", the same shape
  // Task 7 fixed at `/`. A transient 500 on this fetch left `bot` `undefined` forever while
  // `overview` could still resolve, which (after the zero-flash fix below started gating on
  // `bot`) pinned the whole page in `StatsSkeleton`/`TableSkeleton` with no way out and nothing
  // surfaced. `botSettled` is true once the query has an answer either way — success or error —
  // so the skeleton only covers genuine in-flight loading, never a stuck failure.
  const botSettled = !!bot || !!botError;

  // Same query key `BotSwitcher.tsx`/`BotSources.tsx` fetch under — a visit to Overview shares
  // one cache entry with whichever of those already warmed it (the rail's switcher mounts on
  // every route), rather than a fresh fetch. Needed for the header's domain and for "Re-sync".
  const { data: sources } = useQuery({
    queryKey: ['bots', botId, 'sources'],
    queryFn: () => apiFetch<SourceDto[]>(`/api/bots/${botId}/sources`),
    enabled: !!botId,
    staleTime: 60_000,
  });

  // `pollWhileSyncing: true` — Task 2 (dashboard-shell feedback cycle): this is the one screen
  // actually looking at an in-flight crawl, and it finishes server-side with nothing on this
  // page to hang an invalidation off. See `useBotOverview`'s own doc comment for why `RailNav`
  // does NOT pass this (it's mounted for the whole session, not just while someone is watching).
  const { data: overview, isPending, error } = useBotOverview(botId, { pollWhileSyncing: true });
  // Cheap workspace-level query (M6 Task 3, spec D1) — React Query dedupes this against the
  // Billing screen's own `useBillingSummary()` fetch under the same cache key, so visiting
  // Overview never doubles the request when Billing was already open in another tab.
  const { data: billingSummary } = useBillingSummary();

  // See `eligibleForResync`'s own doc comment (`lib/overview.ts`) for why `crawling`/`indexing`/
  // `deleting` are all excluded from the fan-out set below.
  const eligibleSources = eligibleForResync(sources);

  const resyncAll = useMutation({
    mutationFn: async () => {
      if (eligibleSources.length === 0) {
        throw new Error(t.overview.resyncNothingToSync);
      }
      const results = await Promise.allSettled(
        eligibleSources.map((s) => apiFetch<SourceDto>(`/api/bots/${botId}/sources/${s.id}/resync`, { method: 'POST' })),
      );
      if (results.every((r) => r.status === 'rejected')) {
        throw new Error(t.overview.resyncGenericError);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bots', botId, 'sources'] });
      queryClient.invalidateQueries({ queryKey: ['bots', botId, 'overview'] });
    },
  });

  const domain = deriveDomain(sources);

  // Branch on the explicit flag Task 1 added, never on a count: `totals.messages14d === 0`
  // would throw a bot idle for a fortnight back into onboarding (it has history, just none in
  // the last 14 days), and `latestConversations.length === 0` is only accidentally correct
  // today — both are exactly what `everHadConversation` exists to avoid (packages/db/src/repos/
  // overview.test.ts's 90-day-old-conversation case pins this). Requires `bot` too (a second,
  // independently-loading query) since `FirstRun` needs its `publicId`/`createdAt`. If `bot`
  // settles into an error instead of a value, this simply never fires (it needs a real `bot` it
  // doesn't have) — the render below falls through to the graceful-degradation path instead of
  // `FirstRun`, same as it always did before this task, with `botError` now surfaced rather than
  // silently swallowed (see below).
  if (overview && bot && !overview.everHadConversation) {
    return <FirstRun botId={botId} bot={bot} overview={overview} domain={domain} />;
  }

  const knowledgeChip = overview ? knowledgeChipView(overview.knowledge, t) : null;

  const headerSubtitle = bot ? (
    <>
      {domain ? (
        <>
          {t.overview.liveOnPrefix}
          <span className="font-mono text-[12.5px]">{domain}</span> ·{' '}
        </>
      ) : null}
      {t.overview.answeringSince({ date: sinceDate(bot.createdAt) })}
    </>
  ) : undefined;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t.overview.title}
        subtitle={headerSubtitle}
        action={
          botId && (
            <div className="flex items-center gap-2">
              <Button asChild variant="outline" className="h-[34px] px-[13px] text-[13px]">
                <Link to={`/bots/${botId}/playground`}>{t.overview.testInPlaygroundCta}</Link>
              </Button>
              <Button asChild className="h-[34px] px-[13px] text-[13px]">
                <Link to={`/bots/${botId}/sources`}>{t.overview.addSourcesCta}</Link>
              </Button>
            </div>
          )
        }
      />

      {billingSummary && botId && <UsageWarningBanner summary={billingSummary} dismissKey={botId} />}

      {error && <p className="text-destructive">{error.message}</p>}
      {/* `bot` failing doesn't block the page — `headerSubtitle` above already falls back to
          `undefined` without it, and the stats below only need `overview`. It still needs to be
          seen rather than silently leaving the subtitle blank forever with no explanation. */}
      {botError && <p className="text-destructive">{t.overview.botDetailsLoadError({ message: botError.message })}</p>}

      {(isPending || (overview && !botSettled)) && (
        <div className="flex flex-col gap-5">
          <StatsSkeleton />
          <TableSkeleton />
        </div>
      )}

      {overview && botSettled && (
        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-[2fr_1fr_1fr]">
            <div className="flex flex-col rounded-[14px] border bg-card p-5 pb-[14px] shadow-el-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[13px] text-muted-foreground">{t.overview.messagesLabel}</span>
                <Link to={`/bots/${botId}/conversations`} className="text-[12.5px] font-medium text-primary hover:underline">
                  {t.overview.conversationsLink}
                </Link>
              </div>
              <div className="mt-1.5 flex items-baseline gap-2">
                <span className="font-display text-[32px] leading-none font-bold tracking-[-0.02em]">
                  {formatNumber(overview.totals.messages14d, locale)}
                </span>
                <span
                  className={`text-[12.5px] font-semibold ${
                    messagesTrend(overview.messages14d).tone === 'up' ? 'text-st-ready' : 'text-muted-foreground'
                  }`}
                >
                  {messagesTrend(overview.messages14d).label}
                </span>
                <span className="text-xs text-muted-foreground">{t.overview.vsPreviousPeriod}</span>
              </div>
              <div className="relative mt-3.5 min-h-[62px] flex-1">
                <svg
                  viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
                  preserveAspectRatio="none"
                  className="block h-16 w-full overflow-visible"
                  aria-hidden="true"
                >
                  <defs>
                    <linearGradient id="overviewSpark" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--primary)" stopOpacity={0.2} />
                      <stop offset="100%" stopColor="var(--primary)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <polygon points={sparklineArea(overview.messages14d, SPARK_W, SPARK_H)} fill="url(#overviewSpark)" />
                  <polyline
                    points={sparklinePoints(overview.messages14d, SPARK_W, SPARK_H)}
                    fill="none"
                    stroke="var(--primary)"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                  />
                </svg>
              </div>
              {sparklineDateLabels(overview.messages14d) && (
                <div className="mt-1 flex justify-between font-mono text-[10.5px] text-muted-foreground">
                  <span>{sparklineDateLabels(overview.messages14d)!.start}</span>
                  <span>{sparklineDateLabels(overview.messages14d)!.end}</span>
                </div>
              )}
            </div>

            <div className="flex flex-col rounded-[14px] border bg-card p-5 shadow-el-1">
              <span className="text-[13px] text-muted-foreground">{t.overview.leadsLabel}</span>
              <span className="mt-1.5 font-display text-[32px] leading-none font-bold tracking-[-0.02em]">
                {overview.totals.leads}
              </span>
              {/* Triage-state design 2026-08-21 §C — swaps to the "N new" fact (status: 'new'
                  leads, the same figure the rail's Leads pill counts) whenever there IS
                  something new; otherwise keeps today's "this week" line rather than reading
                  "0 new" as if that were itself news. */}
              <span className="mt-1 text-xs text-muted-foreground">
                {overview.totals.newLeads > 0
                  ? t.overview.newLeadsCount({ n: overview.totals.newLeads })
                  : t.overview.leadsThisWeek({ n: overview.totals.leadsThisWeek })}
              </span>
              <Link
                to={`/bots/${botId}/leads`}
                className="mt-auto pt-3 text-[12.5px] font-medium text-primary hover:underline"
              >
                {t.overview.viewLeadsLink}
              </Link>
            </div>

            <div className="flex flex-col rounded-[14px] border border-[color-mix(in_oklch,var(--st-paused)_40%,var(--border))] bg-[color-mix(in_oklch,var(--st-paused)_6%,var(--card))] p-5 shadow-el-1">
              <span className="text-[13px] text-muted-foreground">{t.overview.unansweredLabel}</span>
              <span className="text-st-paused mt-1.5 font-display text-[32px] leading-none font-bold tracking-[-0.02em]">
                {overview.totals.unansweredGroups}
              </span>
              <Link
                to={`/bots/${botId}/unanswered`}
                className="mt-auto pt-3 text-[12.5px] font-medium text-primary hover:underline"
              >
                {t.overview.fixThemLink}
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-[2fr_1fr]">
            <NeedsYou botId={botId} items={overview.topUnanswered} totalCount={overview.totals.unansweredGroups} />

            <div className="flex flex-col rounded-[14px] border bg-card p-5 shadow-el-1">
              <div className="flex items-center justify-between gap-2">
                <h2 className="font-display text-lg font-semibold">{t.overview.knowledgeHeading}</h2>
                {overview.knowledge.sourceCount > 0 && knowledgeChip && (
                  <StatusChip state={knowledgeChip.state} label={knowledgeChip.label} />
                )}
              </div>

              {overview.knowledge.sourceCount === 0 ? (
                <div className="mt-3 flex flex-col items-center gap-1 rounded-lg border border-dashed p-6 text-center">
                  <p className="text-sm font-medium">{t.overview.pointAtSite}</p>
                  <Link to={`/bots/${botId}/sources`} className="text-[12.5px] font-medium text-primary hover:underline">
                    {t.overview.addSourceLink}
                  </Link>
                </div>
              ) : (
                <>
                  <div className="mt-3 flex flex-col text-[13px]">
                    <div className="flex items-baseline justify-between gap-2 border-b py-[7px]">
                      <span className="text-muted-foreground">{t.overview.pagesIndexed}</span>
                      <span className="font-semibold">{overview.knowledge.pageCount}</span>
                    </div>
                    <div className="flex items-baseline justify-between gap-2 border-b py-[7px]">
                      <span className="text-muted-foreground">{t.overview.sourcesCountLabel}</span>
                      <span className="font-semibold">{overview.knowledge.sourceCount}</span>
                    </div>
                    <div className="flex items-baseline justify-between gap-2 py-[7px]">
                      <span className="text-muted-foreground">{t.overview.lastSyncedLabel}</span>
                      <span className="font-semibold">{relativeTime(overview.knowledge.lastSyncedAt, t.time)}</span>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-3.5">
                    <Link to={`/bots/${botId}/sources`} className="text-[12.5px] font-medium text-primary hover:underline">
                      {t.overview.manageSourcesLink}
                    </Link>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 text-muted-foreground"
                      disabled={resyncAll.isPending || !sources || eligibleSources.length === 0}
                      onClick={() => resyncAll.mutate()}
                    >
                      {resyncAll.isPending
                        ? t.overview.resyncing
                        : sources && eligibleSources.length === 0
                          ? t.overview.allSyncing
                          : t.overview.resyncCta}
                    </Button>
                  </div>
                  {resyncAll.isError && (
                    <p className="mt-2 text-[12px] text-destructive">
                      {resyncAll.error instanceof Error ? resyncAll.error.message : t.overview.resyncGenericError}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="flex flex-col overflow-hidden rounded-[14px] border bg-card shadow-el-1">
            <div className="flex items-center justify-between gap-2.5 px-5 pt-[15px] pb-3">
              <h2 className="font-display text-lg font-semibold">{t.overview.latestConversationsHeading}</h2>
              <Link to={`/bots/${botId}/conversations`} className="text-[12.5px] font-medium text-primary hover:underline">
                {t.overview.allConversationsLink}
              </Link>
            </div>
            {overview.latestConversations.length === 0 ? (
              <p className="px-5 pb-5 text-[13px] text-muted-foreground">{t.overview.noConversationsEmpty}</p>
            ) : (
              overview.latestConversations.map((conv, i) => {
                const tag = conversationTag(conv, t);
                return (
                  <Link
                    key={conv.id}
                    to={`/bots/${botId}/conversations?open=${conv.id}`}
                    className={`flex h-11 items-center gap-3.5 px-5 transition-colors hover:bg-accent ${i === 0 ? '' : 'border-t'}`}
                  >
                    {/* 44px fits compactRelativeTime's widest realistic output ("999d",
                        4 mono chars) — the column was 74px and the reclaimed width belongs
                        to the snippet, the row's most informative cell (Johnny, 2026-08-20). */}
                    <span className="w-[44px] flex-none font-mono text-[11.5px] text-muted-foreground">
                      {compactRelativeTime(conv.startedAt)}
                    </span>
                    <span className="min-w-0 flex-1 overflow-hidden text-[13.5px] text-ellipsis whitespace-nowrap">
                      {conv.snippet ? `"${conv.snippet}"` : t.overview.noMessageFallback}
                    </span>
                    <span className="flex-none font-mono text-[11.5px] text-muted-foreground">{conv.messageCount}</span>
                    <span
                      className={`w-[104px] flex-none text-right text-[11.5px] font-medium ${tag?.className ?? 'text-transparent'}`}
                    >
                      {tag?.label ?? ''}
                    </span>
                  </Link>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
